import { a as __rest } from '../../_tslib.esm.js';
import { jsxs, jsx } from 'react/jsx-runtime';
import React__default, { forwardRef, useState, useId, useCallback } from 'react';
import { XMarkIcon, ChevronLeftIcon } from '@iris/react-icons';
import * as RadixDialog from '@radix-ui/react-dialog';
import sheetStyles from './Sheet.styles.esm.js';
import { ControlButton } from '../ControlButton/ControlButton.esm.js';

const Sheet = forwardRef((_a, ref) => {
  var {
      title,
      desktopPosition = "center",
      trigger,
      children,
      footerContent,
      className,
      fixedButtons = false,
      hasHeader = false,
      showBackArrow = false,
      modal = true,
      onOpenChange,
      onBackClick,
      "aria-label": ariaLabel = ""
    } = _a,
    props = __rest(_a, ["title", "desktopPosition", "trigger", "children", "footerContent", "className", "fixedButtons", "hasHeader", "showBackArrow", "modal", "onOpenChange", "onBackClick", "aria-label"]);
  const {
    overlay,
    content,
    contentWrapper,
    header,
    footerSheet,
    close,
    backIconBox,
    body,
    iconClass,
    title: titleClass
  } = sheetStyles({
    fixedButtons,
    hasHeader,
    showBackArrow,
    modal,
    desktopPosition,
    hasTitle: !!title,
    class: className
  });
  const [open, setOpen] = useState(false);
  const contentId = useId();
  const titleId = useId();
  const handleInteractOutside = React__default.useCallback(event => {
    event.preventDefault();
  }, []);
  const handleOpenChange = useCallback(isOpen => {
    setOpen(isOpen);
    onOpenChange === null || onOpenChange === void 0 ? void 0 : onOpenChange(isOpen);
  }, [onOpenChange]);
  // Disable page scrolling when the overlay is open
  React__default.useEffect(() => {
    if (open) {
      if (modal) {
        document.body.classList.add("iris:overflow-hidden");
      } else {
        document.body.classList.add("iris:md:overflow-hidden");
      }
    } else {
      document.body.classList.remove("iris:overflow-hidden", "iris:md:overflow-hidden");
    }
    // Cleanup on unmount
    return () => {
      document.body.classList.remove("iris:overflow-hidden", "iris:md:overflow-hidden");
    };
  }, [open, modal]);
  const renderHeader = () => jsxs("div", {
    className: header(),
    children: [hasHeader && jsx(ControlButton, {
      className: backIconBox(),
      icon: jsx(ChevronLeftIcon, {
        className: iconClass()
      }),
      onClick: onBackClick,
      "aria-label": "back"
    }), title && jsx(RadixDialog.Title, {
      asChild: true,
      children: jsx("div", {
        className: titleClass(),
        id: "title" + titleId,
        children: title
      })
    }), hasHeader && jsx(RadixDialog.Close, {
      asChild: true,
      children: jsx(ControlButton, {
        className: close(),
        icon: jsx(XMarkIcon, {
          className: iconClass()
        }),
        "aria-label": "close"
      })
    })]
  });
  const renderBody = () => jsx("div", {
    className: body(),
    "aria-live": "polite",
    "aria-atomic": "true",
    id: children ? contentId : undefined,
    tabIndex: -1,
    children: children
  });
  const renderFixedFooter = () => jsx("div", {
    className: footerSheet(),
    children: footerContent
  });
  const Content = jsxs(RadixDialog.Content, {
    ref: ref,
    className: content(),
    "aria-modal": modal,
    "aria-describedby": children ? contentId : undefined,
    onInteractOutside: handleInteractOutside,
    children: [renderHeader(), !hasHeader && jsx(RadixDialog.Close, {
      asChild: true,
      children: jsx(ControlButton, {
        className: close(),
        icon: jsx(XMarkIcon, {
          className: iconClass()
        }),
        "aria-label": "close"
      })
    }), jsx(RadixDialog.Title, {}), jsx(RadixDialog.Description, {}), renderBody(), fixedButtons && renderFixedFooter()]
  });
  return jsxs(RadixDialog.Root, Object.assign({
    open: open,
    onOpenChange: handleOpenChange,
    modal: false
  }, props, {
    children: [trigger && jsx(RadixDialog.Trigger, {
      asChild: true,
      "aria-controls": open ? contentId : undefined,
      children: trigger
    }), jsxs(RadixDialog.DialogPortal, {
      children: [jsx("div", {
        "data-state": open ? "open" : "closed",
        className: overlay(),
        onClick: () => setOpen(false)
      }), jsx("div", {
        "data-state": open ? "open" : "closed",
        className: contentWrapper(),
        children: Content
      })]
    })]
  }));
});
Sheet.displayName = "Sheet";

export { Sheet };
